import { useTheme } from '../hooks/useTheme';
import { FileSearch, Sparkles, FileText, BarChart3, History, Target } from "lucide-react";

// Verbatim copy of the Coming Soon page pushed directly to main (commit
// 01ca6cc, "feat: add Resume Analysis Coming Soon placeholder page for
// production (#107)"), kept as its own component so it can be used as the
// production override without touching the real ResumeAnalysis.js, which
// still has the full in-progress feature on dev.
export default function ResumeAnalysisComingSoon() {
    const { isDarkMode } = useTheme();

    const upcomingFeatures = [
        {
            icon: FileText,
            title: "AI Resume Analysis",
            description: "Upload your resume and get detailed AI-powered feedback on content, structure, and impact.",
            color: "blue"
        },
        {
            icon: Target,
            title: "Job Description Matching",
            description: "Paste a job description and see how well your resume aligns with the role's requirements.",
            color: "emerald"
        },
        {
            icon: BarChart3,
            title: "Scored Report Card",
            description: "Receive a scored breakdown across key categories like achievements, leadership, and relevance.",
            color: "purple"
        },
        {
            icon: History,
            title: "Review History",
            description: "Track your past analyses and see how your resume improves over time.",
            color: "amber"
        }
    ];

    const colorClasses = {
        blue: {
            bg: isDarkMode ? "bg-blue-900/30" : "bg-blue-50",
            border: isDarkMode ? "border-blue-700" : "border-blue-200",
            icon: isDarkMode ? "text-blue-400" : "text-blue-600",
            text: isDarkMode ? "text-blue-200" : "text-blue-900"
        },
        emerald: {
            bg: isDarkMode ? "bg-primary-900/30" : "bg-primary-50",
            border: isDarkMode ? "border-primary-700" : "border-primary-200",
            icon: isDarkMode ? "text-primary-400" : "text-primary-600",
            text: isDarkMode ? "text-primary-200" : "text-primary-900"
        },
        purple: {
            bg: isDarkMode ? "bg-purple-900/30" : "bg-purple-50",
            border: isDarkMode ? "border-purple-700" : "border-purple-200",
            icon: isDarkMode ? "text-purple-400" : "text-purple-600",
            text: isDarkMode ? "text-purple-200" : "text-purple-900"
        },
        amber: {
            bg: isDarkMode ? "bg-amber-900/30" : "bg-amber-50",
            border: isDarkMode ? "border-amber-700" : "border-amber-200",
            icon: isDarkMode ? "text-amber-400" : "text-amber-600",
            text: isDarkMode ? "text-amber-200" : "text-amber-900"
        }
    };

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-4xl mx-auto px-4 py-8">
                <header className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <FileSearch className={isDarkMode ? 'text-primary-400' : 'text-primary-600'} size={28} />
                        <h1 className={`text-3xl font-extrabold tracking-tight ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Resume Analysis</h1>
                    </div>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>AI-powered resume review to help you stand out</p>
                </header>

                <div className={`border-2 rounded-2xl p-8 mb-8 text-center shadow-sm ${
                    isDarkMode
                        ? 'bg-primary-900/30 border-primary-700'
                        : 'bg-primary-50 border-primary-200'
                }`}>
                    <div className="flex items-center justify-center gap-3 mb-3">
                        <Sparkles className={isDarkMode ? 'text-primary-400' : 'text-primary-600'} size={28} />
                        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-primary-200' : 'text-primary-900'}`}>Coming Soon</h2>
                    </div>
                    <p className={`text-lg max-w-2xl mx-auto ${isDarkMode ? 'text-primary-300' : 'text-primary-800'}`}>
                        We're putting the finishing touches on Resume Analysis. Upload your resume, compare it against job descriptions, and get a detailed AI-powered report card with actionable feedback.
                    </p>
                </div>

                <section>
                    <h2 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>What to Expect</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {upcomingFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            const colors = colorClasses[feature.color];
                            return (
                                <div
                                    key={index}
                                    className={`rounded-2xl border ${colors.bg} ${colors.border} p-6 shadow-sm hover:shadow-md transition-shadow`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 ${colors.bg} rounded-xl ${colors.icon}`}>
                                            <Icon size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className={`font-bold text-lg mb-2 ${colors.text}`}>{feature.title}</h3>
                                            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{feature.description}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>
        </div>
    );
}
